import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { getConnectorIconForVariant } from "@/components/connectors/providerIcons.tsx";
import { useConnectorCatalog } from "@/components/connectors/useConnectorCatalog.ts";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import {
  connectorDisplayTitle,
  connectorPickerSections,
  matchesConnectorSearch,
} from "@/shared/connectors/pickerSections.ts";
import { cn } from "@/lib/utils.ts";
import type { ConnectorCatalogItem } from "@/types/connectors.ts";
import { PickerSearchInput } from "./picker-search-input.tsx";
import {
  handlePickerSubmit,
  PICKER_HEADER_CLASS,
  scrollToItem,
  usePickerKeyboard,
} from "./usePickerKeyboard.ts";

/** No scoping or pinning applies in the guided flow — the full name-sorted catalog. */
const NO_FRAMING = { scopeIds: [], pinnedIds: [], preferredIds: [] };

interface ConnectorServicePickerProps {
  /** Called with the chosen service name (e.g. "Notion"). */
  onSelect: (serviceName: string) => void;
  defaultValue?: string | null;
}

/**
 * Single-select service picker for the connector guided flow.
 *
 * Lists the live connector catalog (same query as Settings and the in-thread
 * picker card, so a connect elsewhere flips rows here too) with type-to-filter
 * and free-text fallback for services the catalog does not carry — the agent
 * resolves those by name. Rows the connect surfaces hide (connected, healthy,
 * not repeatable) are filtered out, since the backend refuses to reconnect
 * them. A previously chosen service seeds the search box, so back-navigation
 * restores both catalog picks and free-text entries.
 */
export function ConnectorServicePicker({ onSelect, defaultValue }: ConnectorServicePickerProps) {
  // The picker owns its scroll container (the list below the header), so the
  // ref is direct rather than a walk up to the dropdown shell's scroller.
  const listRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isError } = useConnectorCatalog({ showToasts: false });
  const [freeText, setFreeText] = useState(defaultValue ?? "");

  const sorted = useMemo(() => {
    const { pinned, rest } = connectorPickerSections(data?.items ?? [], NO_FRAMING);
    return [...pinned, ...rest].filter((item) => !connectorConnectMode(item).hidden);
  }, [data]);

  const items = useMemo(() => {
    const query = freeText.trim().toLowerCase();
    return sorted.filter(
      (item) =>
        matchesConnectorSearch(item, freeText) ||
        connectorDisplayTitle(item).toLowerCase().includes(query),
    );
  }, [sorted, freeText]);

  const {
    selectedIndex,
    setSelectedIndex,
    handleKeyDown: handlePickerKeyDown,
  } = usePickerKeyboard({
    itemCount: items.length,
    onSelect: (index) => {
      const item = items[index];
      if (item) onSelect(connectorDisplayTitle(item));
    },
  });

  // Whether the last highlight move came from the keyboard (arrows, filter
  // reset) rather than hover. Scroll-into-view must only follow keyboard
  // moves: wheel-scrolling hovers a new row each tick, and snapping that row
  // into view cancels the user's scroll.
  const followHighlightRef = useRef(false);

  // Re-filtering can keep the list the same length while its rows shift, so the
  // highlight must restart at the top on every query change — the clamp inside
  // usePickerKeyboard only fires when the count shrinks.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on query change
  useEffect(() => {
    followHighlightRef.current = true;
    setSelectedIndex(0);
  }, [freeText]);

  useEffect(() => {
    if (!followHighlightRef.current) return;
    scrollToItem(listRef.current, selectedIndex);
  }, [selectedIndex]);

  const handleHover = useCallback(
    (index: number) => {
      followHighlightRef.current = false;
      setSelectedIndex(index);
    },
    [setSelectedIndex],
  );

  // Arrow keys delegate to the shared picker keyboard hook; Enter/Tab commit
  // the highlighted service or fall back to the typed free text. While the
  // catalog loads, Enter/Tab are consumed so they cannot bubble to the
  // dropdown's Tab-to-skip and strand a half-composed message.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        followHighlightRef.current = true;
        handlePickerKeyDown(e);
        return;
      }
      const isSubmitKey = e.key === "Enter" || (e.key === "Tab" && !e.shiftKey);
      if (isLoading && isSubmitKey) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      handlePickerSubmit(e, {
        filteredOptions: items.map(connectorDisplayTitle),
        selectedIndex,
        freeText,
        onSelect,
      });
    },
    [items, selectedIndex, freeText, onSelect, handlePickerKeyDown, isLoading],
  );

  return (
    <div
      tabIndex={-1}
      className="flex h-full min-h-0 flex-col outline-none"
      role="listbox"
      aria-label="Connector services"
    >
      {/* The header sits outside the scrollable region so the list scrolls
          beneath it: a sticky header inside the scroller would let rows (and
          the scrollbar) run behind it, and scroll-into-view math would count
          the covered strip as visible. */}
      <div className={cn(PICKER_HEADER_CLASS, "shrink-0")}>
        <PickerSearchInput
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onClear={() => setFreeText("")}
          placeholder="Search services..."
          autoFocus
        />
      </div>

      <div
        ref={listRef}
        className="relative min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-border"
      >
        {isLoading && <p className="px-3 py-2 text-sm text-muted-foreground">Loading services…</p>}

        {isError && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            Couldn't load services. Type a name and press Enter to use it.
          </p>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {freeText.trim()
              ? "No matching services. Enter sends the name as typed."
              : "No services are available yet."}
          </p>
        )}

        {items.map((item, i) => (
          <ConnectorServiceRow
            key={item.id}
            item={item}
            index={i}
            selected={i === selectedIndex}
            onSelect={onSelect}
            onHover={handleHover}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One service row: brand mark (favicon fallback), name, and the item's
 * connection status. Click selects; moving the pointer over a row moves the
 * keyboard highlight — via mousemove, not mouseenter, because keyboard
 * scroll-follow slides rows under a stationary cursor and the resulting
 * mouseenter would steal the highlight straight back. Memoized so arrowing
 * through the list re-renders only the two rows whose `selected` flag flips,
 * not every logo in the catalog.
 */
const ConnectorServiceRow = memo(function ConnectorServiceRow({
  item,
  index,
  selected,
  onSelect,
  onHover,
}: {
  item: ConnectorCatalogItem;
  index: number;
  selected: boolean;
  onSelect: (serviceName: string) => void;
  onHover: (index: number) => void;
}) {
  const { needsReconnect } = connectorConnectMode(item);
  const statusLabel = needsReconnect ? "Reconnect" : item.connected ? "Connected" : null;
  const brandIcon = item.provider
    ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
    : null;

  return (
    <div
      data-index={index}
      role="option"
      tabIndex={-1}
      aria-selected={selected}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer",
        selected && "bg-secondary",
      )}
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(connectorDisplayTitle(item));
      }}
      onMouseMove={selected ? undefined : () => onHover(index)}
    >
      <span className="flex size-6 shrink-0 items-center justify-center">
        {brandIcon ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} size="sm" />}
      </span>
      <span className="min-w-0 truncate">{connectorDisplayTitle(item)}</span>
      {statusLabel && (
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">{statusLabel}</span>
      )}
    </div>
  );
});
